import type { TransactionActionEligibleTransaction } from '../../proposalTypes';
import type { RenamePayeeProposal } from './types';

export const parseRenamePayeeAssistantResponse = (
  eligibleTransactions: TransactionActionEligibleTransaction[],
  assistantResponse: string,
): RenamePayeeProposal[] | null => {
  const trimmedAssistantResponse = assistantResponse.trim();
  if (!trimmedAssistantResponse) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmedAssistantResponse);
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== 'object' || !Array.isArray((parsed as Record<string, unknown>).proposals)) {
    return null;
  }

  return validateRenamePayeeProposals(eligibleTransactions, parsed);
};

export const validateRenamePayeeProposals = (
  eligibleTransactions: TransactionActionEligibleTransaction[],
  parsedResponse: unknown,
): RenamePayeeProposal[] => {
  if (!parsedResponse || typeof parsedResponse !== 'object') {
    return [];
  }

  const rawProposals = (parsedResponse as Record<string, unknown>).proposals;
  if (!Array.isArray(rawProposals)) {
    return [];
  }

  const eligibleById = new Map(eligibleTransactions.map((transaction) => [transaction.transactionId, transaction]));
  const seenTransactionIds = new Set<string>();
  const proposals: RenamePayeeProposal[] = [];

  rawProposals.forEach((rawProposal) => {
    if (!rawProposal || typeof rawProposal !== 'object') {
      return;
    }

    const candidate = rawProposal as Record<string, unknown>;
    const { transactionId, proposedPayee, rationale, confidence } = candidate;

    if (typeof transactionId !== 'string' || typeof proposedPayee !== 'string' || typeof rationale !== 'string') {
      return;
    }

    const eligibleTransaction = eligibleById.get(transactionId);
    if (!eligibleTransaction || seenTransactionIds.has(transactionId)) {
      return;
    }

    const trimmedProposedPayee = proposedPayee.trim();
    if (!trimmedProposedPayee || trimmedProposedPayee === eligibleTransaction.payee) {
      return;
    }

    seenTransactionIds.add(transactionId);
    proposals.push({
      transactionId,
      currentPayee: eligibleTransaction.payee,
      proposedPayee: trimmedProposedPayee,
      confidence: typeof confidence === 'number' ? Math.min(1, Math.max(0, confidence)) : 0,
      rationale,
    });
  });

  return proposals;
};
